/* eslint-disable react-refresh/only-export-components */
import React from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";

export function initFrontendMonitoring() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  
  // Do not report to Sentry when running locally
  if (import.meta.env.MODE === "development" || import.meta.env.MODE === "local") return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_GIT_COMMIT_SHA || "unknown",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      // Case-insensitive header scrubbing
      if (event.request?.headers) {
        const sensitiveHeaders = ["authorization", "cookie", "set-cookie", "x-api-key"];
        for (const key of Object.keys(event.request.headers)) {
          if (sensitiveHeaders.includes(key.toLowerCase())) {
            delete event.request.headers[key];
          }
        }
      }
      return event;
    },
  });
}

/**
 * Observability Error Boundary compliant with Homy i18n rules (AGENTS.md).
 * Uses localized translation key rather than hardcoded English strings.
 */
const FallbackErrorUI = () => {
  const { t } = useTranslation("common");
  return (
    <div className="p-6 text-foreground bg-background border border-border rounded-lg shadow-sm m-4">
      <h2 className="text-lg font-semibold text-destructive mb-2">
        {t("errors.ui_error_title", "An unexpected UI error occurred")}
      </h2>
      <p className="text-muted-foreground text-sm">
        {t("errors.unexpected_ui_error", "An unexpected error occurred. Our engineering team has been notified.")}
      </p>
      <button 
        onClick={() => window.location.reload()} 
        className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium"
      >
        {t("common.reload_page", "Reload Page")}
      </button>
    </div>
  );
};

export const ObservabilityErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary fallback={<FallbackErrorUI />}>
    {children}
  </Sentry.ErrorBoundary>
);

/**
 * Sets Sentry user context for error tracking and session attribution.
 * Handles both user objects and null (clear).
 * @param {{ id?: string, userId?: string, email?: string, role?: string, organizationId?: string, organization_id?: string } | null} user
 */
export function setMonitoringUser(user) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id || user.userId,
    email: user.email,
    role: user.role,
    ...(user.organizationId || user.organization_id
      ? { organization_id: user.organizationId || user.organization_id }
      : {}),
  });
}

/**
 * Clears Sentry user context on logout or session reset.
 */
export function clearMonitoringUser() {
  Sentry.setUser(null);
}

export { Sentry };

